// components/modals/BankCardModals/CardFormModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Tab, Tabs } from 'react-bootstrap';
import { isValidExpDate } from '../../../services/bankcard.service';
import useModalHistory from '../../../hooks/useModalHistory';

const CardFormModal = ({
  show,
  onHide,
  initialData,
  isEditing = false,
  onSave,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    alias: '',
    cardNumber: '',
    cardCcv: '',
    cardExp: '',
    cardHolderName: '',
    accountNumber: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  });

  useModalHistory(show, onHide, 'card-form-modal');

  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('card-info-tab');

  useEffect(() => {
    if (initialData) {
      // Ensure all values are strings before setting in formData
      const sanitizedData = Object.keys(initialData).reduce((acc, key) => {
        // Convert values to strings where needed
        acc[key] = initialData[key] != null ? String(initialData[key]) : '';
        return acc;
      }, {});

      setFormData(sanitizedData);
    } else {
      // Reset form for new entries
      setFormData({
        alias: '',
        cardNumber: '',
        cardCcv: '',
        cardExp: '',
        cardHolderName: '',
        accountNumber: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        country: ''
      });
    }
    // Reset UI state
    setShowCardNumber(false);
    setShowCvv(false);
    setErrors({});
    setActiveTab('card-info-tab');
  }, [initialData, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for card expiration date
    if (name === 'cardExp') {
      let formattedValue = value;
      // Add slash after MM if not already present
      if (value.length === 2 && !value.includes('/')) {
        formattedValue = value + '/';
      }
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Safely check if alias is empty by converting to string first
    const aliasStr = String(formData.alias || '');
    if (!aliasStr.trim()) {
      newErrors.alias = 'Card alias is required';
    }

    // Safely check if cardNumber is empty by converting to string first
    const cardNumberStr = String(formData.cardNumber || '');
    if (!cardNumberStr.trim()) {
      newErrors.cardNumber = 'Card number is required';
    }

    // Safely check if cardCcv is empty by converting to string first
    const cardCcvStr = String(formData.cardCcv || '');
    if (!cardCcvStr.trim()) {
      newErrors.cardCcv = 'CVV/CVC is required';
    }

    // Safely check if cardExp is empty by converting to string first
    const cardExpStr = String(formData.cardExp || '');
    if (!cardExpStr.trim()) {
      newErrors.cardExp = 'Expiration date is required';
    } else if (!isValidExpDate(cardExpStr)) {
      newErrors.cardExp = 'Invalid format (use MM/YY)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Show first tab if there are errors there
      if (errors.alias || errors.cardNumber || errors.cardCcv || errors.cardExp || errors.cardHolderName || errors.accountNumber) {
        setActiveTab('card-info-tab');
      }
      return;
    }

    try {
      await onSave(formData, initialData?.originalName);
    } catch (error) {
      console.error('Error saving card:', error);
      // You could set a general error here if needed
    }
  };

  // Calculate masked values for preview
  const cardNumber = String(formData.cardNumber || '');
  const maskedCardNumber = cardNumber
    ? '•••• •••• •••• ' + cardNumber.slice(-4)
    : '•••• •••• •••• ••••';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="common-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Bank Card' : 'Add Bank Card'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Add a style tag to override the problematic CSS */}
        <style>
          {`
            /* Override the problematic CSS from forgot-password.css */
            .tab-content {
              display: block !important;
            }
          `}
        </style>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          id="card-form-tabs"
        >
          <Tab eventKey="card-info-tab" title="Card Information">
            <div className="tab-content-body">
              {/* Card Preview (for mobile) */}
              <div className="card-preview mb-4 d-md-none">
                <div className="credit-card">
                  <div className="card-top">
                    <div className="card-brand">
                      <i className="far fa-credit-card"></i>
                    </div>
                    <div className="card-alias">
                      {formData.alias}
                    </div>
                  </div>
                  <div className="card-number">
                    {showCardNumber ? formData.cardNumber : maskedCardNumber}
                  </div>
                  <div className="card-bottom">
                    <div className="card-holder">
                      {formData.cardHolderName}
                    </div>
                    <div className="card-exp">
                      {formData.cardExp}
                    </div>
                  </div>
                </div>
              </div>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Card Alias*</Form.Label>
                  <Form.Control
                    type="text"
                    name="alias"
                    value={formData.alias}
                    onChange={handleChange}
                    className={errors.alias ? 'is-invalid' : ''}
                    placeholder="e.g., Personal Visa"
                    required
                  />
                  {errors.alias && <div className="invalid-feedback">{errors.alias}</div>}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Cardholder Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="cardHolderName"
                    value={formData.cardHolderName}
                    onChange={handleChange}
                    placeholder="e.g., John Doe"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="accountNumber"
                    maxLength={20}
                    autoComplete="off" 
                    value={formData.accountNumber}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Card Number*</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showCardNumber ? "number" : "password"}
                      name="cardNumber"
                      maxLength={19}
                      minLength={10}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.cardNumber}
                      autoComplete="cc-number"
                      onChange={handleChange}
                      className={errors.cardNumber ? 'is-invalid' : ''}
                      placeholder="Card number"
                      required
                    />
                    <InputGroup.Text
                      onClick={() => setShowCardNumber(!showCardNumber)}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className={`fas fa-${showCardNumber ? 'eye-slash' : 'eye'}`}></i>
                    </InputGroup.Text>
                    {errors.cardNumber && <div className="invalid-feedback">{errors.cardNumber}</div>}
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>CVV/CVC*</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showCvv ? "text" : "password"}
                      name="cardCcv"
                      maxLength={4}
                      minLength={3}
                      value={formData.cardCcv}
                      onChange={handleChange}
                      className={errors.cardCcv ? 'is-invalid' : ''}
                      placeholder="CVV/CVC"
                      required
                    />
                    <InputGroup.Text
                      onClick={() => setShowCvv(!showCvv)}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className={`fas fa-${showCvv ? 'eye-slash' : 'eye'}`}></i>
                    </InputGroup.Text>
                    {errors.cardCcv && <div className="invalid-feedback">{errors.cardCcv}</div>}
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Expiration Date*</Form.Label>
                  <Form.Control
                    type="text"
                    name="cardExp"
                    value={formData.cardExp}
                    onChange={handleChange}
                    className={errors.cardExp ? 'is-invalid' : ''}
                    placeholder="MM/YY"
                    maxLength={5}
                    required
                  />
                  {errors.cardExp && <div className="invalid-feedback">{errors.cardExp}</div>}
                </Form.Group>
              </Form>
            </div>
          </Tab>

          <Tab eventKey="billing-address-tab" title="Billing Address">
            <div className="tab-content-body">
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Street</Form.Label>
                  <Form.Control
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    placeholder="Street address"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State/Province"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>ZIP/Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    placeholder="ZIP/Postal Code"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                  />
                </Form.Group>
              </Form>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} className="modal-close-btn">
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={isLoading}
          className="modal-action-btn"
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CardFormModal;